import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';
import {
    ACTION_QUILLDOCUMENT_LOAD_BY_ID,
    ACTION_QUILLDOCUMENT_LOAD_BY_TYPE_AND_PAGE,
    ACTION_QUILLDOCUMENT_SEARCH,
    ACTION_QUILLDOCUMENT_LOAD_FAILURE,
    ACTION_QUILLDOCUMENT_LOAD_SUCCESS,
    ACTION_QUILLDOCUMENT_SEARCH_FAILURE,
    ACTION_QUILLDOCUMENT_SEARCH_SUCCESS,

    ACTION_QUILLDOCUMENT_SAVE,
    ACTION_QUILLDOCUMENT_SAVE_SUCCESS,
    ACTION_QUILLDOCUMENT_SAVE_FAILURE,

    ACTION_QUILLDOCUMENT_CREATE,
    ACTION_QUILLDOCUMENT_CREATE_SUCCESS,
    ACTION_QUILLDOCUMENT_CREATE_FAILURE,

    ACTION_QUILLDOCUMENT_VALIDATE,
    ACTION_QUILLDOCUMENT_VALIDATE_SUCCESS,
    ACTION_QUILLDOCUMENT_VALIDATE_FAILURE,
    
    ACTION_QUILLDOCUMENT_CLEAR,
    ACTION_QUILLDOCUMENT_FILTER,

    QuillDocumentAction,
} from '../actions/quillDocument';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IQuillDocumentState {
    formData: Dtos.QuillDocument | Dtos.QuillDocument[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.QuillDocument[];
    searchResults: Dtos.QuillDocument[];
}

const initialState: IQuillDocumentState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    searchResults: []
};

function loading(state: IQuillDocumentState, _isLoading: boolean): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: IQuillDocumentState,
    formData: Dtos.QuillDocument | Dtos.QuillDocument[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function searchSuccess(state: IQuillDocumentState,
    searchResults: Dtos.QuillDocument[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        searchResults: {
            $set: searchResults
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: IQuillDocumentState, formFilter: IFormFilter): IQuillDocumentState {

    const lexicalDocument: Dtos.QuillDocument[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = lexicalDocument ? lexicalDocument.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.editorState.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: IQuillDocumentState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: IQuillDocumentState, responseStatus: Dtos.ResponseStatus): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function searchFailure(state: IQuillDocumentState, responseStatus: Dtos.ResponseStatus): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: IQuillDocumentState, propertiesToClear?: string[]): IQuillDocumentState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: IQuillDocumentState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IQuillDocumentState, _isSaving: boolean): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IQuillDocumentState,
    formData: Dtos.QuillDocument | Dtos.QuillDocument[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IQuillDocumentState, responseStatus: Dtos.ResponseStatus): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IQuillDocumentState, _isCreating: boolean): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IQuillDocumentState,
    formData: Dtos.QuillDocument,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IQuillDocumentState, responseStatus: Dtos.ResponseStatus): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: IQuillDocumentState, _isValidating: boolean): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IQuillDocumentState,
    formData: Dtos.QuillDocument,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IQuillDocumentState, responseStatus: Dtos.ResponseStatus): IQuillDocumentState {
    const newState: IQuillDocumentState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const QuillDocumentReducer: Reducer<IQuillDocumentState> = (state: IQuillDocumentState = initialState, action: QuillDocumentAction) => {
    switch (action.type) {
        case ACTION_QUILLDOCUMENT_LOAD_BY_ID:
        case ACTION_QUILLDOCUMENT_LOAD_BY_TYPE_AND_PAGE:
        case ACTION_QUILLDOCUMENT_SEARCH:
            return loading(state, true);
        case ACTION_QUILLDOCUMENT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_QUILLDOCUMENT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_QUILLDOCUMENT_SEARCH_SUCCESS:
            return searchSuccess(state, action.searchResult, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_QUILLDOCUMENT_SEARCH_FAILURE:
            return searchFailure(state, action.responseStatus);
        case ACTION_QUILLDOCUMENT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_QUILLDOCUMENT_SAVE:
            return saving(state, true);
        case ACTION_QUILLDOCUMENT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_QUILLDOCUMENT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_QUILLDOCUMENT_CREATE:
            return creating(state, true);
        case ACTION_QUILLDOCUMENT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_QUILLDOCUMENT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_QUILLDOCUMENT_VALIDATE:
            return validating(state, true);
        case ACTION_QUILLDOCUMENT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_QUILLDOCUMENT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_QUILLDOCUMENT_FILTER:
            return filter(state, action.formFilter);
    }

    return state;
}

export default QuillDocumentReducer;